.shell {
  position: relative;
  min-height: 100dvh;
  overflow: hidden;
  padding: max(1.25rem, env(safe-area-inset-top)) max(1.25rem, env(safe-area-inset-right)) max(1.25rem, env(safe-area-inset-bottom)) max(1.25rem, env(safe-area-inset-left));
  background:
    radial-gradient(circle at 14% 8%, rgb(55 111 148 / 55%), transparent 38%),
    radial-gradient(circle at 82% 18%, rgb(75 126 160 / 22%), transparent 34%),
    radial-gradient(circle at 76% 82%, rgb(10 22 40 / 72%), transparent 42%),
    linear-gradient(145deg, #162b3c 0%, #24465d 48%, var(--color-navy-deep) 100%);
}

.ambient {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image: linear-gradient(rgb(255 255 255 / 2.3%) 1px, transparent 1px), linear-gradient(90deg, rgb(255 255 255 / 2.3%) 1px, transparent 1px);
  background-size: 54px 54px;
  mask-image: linear-gradient(to bottom, black, transparent 76%);
}

.header, .hero, .footer {
  position: relative;
  z-index: 1;
  width: min(1180px, 100%);
  margin-inline: auto;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.5rem 0;
}

.brand {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  letter-spacing: 0.22em;
  font-size: clamp(0.9rem, 1vw, 1.05rem);
  font-weight: 650;
}

.brandDot {
  width: 0.4rem;
  height: 0.4rem;
  border-radius: 999px;
  background: var(--color-gold);
  box-shadow: 0 0 1.25rem color-mix(in srgb, var(--color-gold) 58%, transparent);
}

.brandMls, .eyebrow, .cardKicker { color: var(--color-gold); }

.environment {
  border: 1px solid var(--color-border-soft);
  border-radius: 999px;
  padding: 0.55rem 0.85rem;
  color: var(--color-text-secondary);
  font-size: 0.8rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: rgb(255 255 255 / 4%);
  backdrop-filter: blur(16px);
}

.hero {
  display: grid;
  gap: clamp(2.25rem, 6vw, 5.5rem);
  padding-block: clamp(4rem, 11vh, 8rem) clamp(2rem, 7vh, 5rem);
}

.copy { max-width: 840px; }

.eyebrow, .cardKicker {
  margin: 0 0 0.85rem;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.13em;
  text-transform: uppercase;
}

.copy h1 {
  max-width: 900px;
  margin: 0;
  font-size: clamp(2.7rem, 7.2vw, 6.8rem);
  line-height: 0.98;
  letter-spacing: -0.05em;
  text-wrap: balance;
}

.lede {
  max-width: 700px;
  margin: 1.6rem 0 0;
  color: var(--color-text-secondary);
  font-size: clamp(1rem, 1.7vw, 1.2rem);
  line-height: 1.7;
}

.grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.25rem;
}

.card {
  display: grid;
  align-content: start;
  gap: 1.35rem;
  min-height: 32rem;
  border: 1px solid var(--color-border-soft);
  border-radius: 1.75rem;
  padding: clamp(1.35rem, 3vw, 2.25rem);
  background:
    linear-gradient(155deg, rgb(255 255 255 / 13%), rgb(255 255 255 / 4.5%)),
    linear-gradient(135deg, rgb(45 93 121 / 18%), rgb(10 22 40 / 16%));
  box-shadow: 0 1.75rem 5rem rgb(5 14 25 / 28%), inset 0 1px rgb(255 255 255 / 8%);
  backdrop-filter: blur(26px) saturate(118%);
}

.card h2 {
  margin: 0;
  font-size: clamp(1.65rem, 3vw, 2.35rem);
  letter-spacing: -0.025em;
}

.cardText {
  margin: 0.8rem 0 0;
  color: var(--color-text-secondary);
  line-height: 1.65;
}

.primaryButton, .secondaryButton {
  width: 100%;
  min-height: 3.25rem;
  border-radius: 1rem;
  border: 1px solid transparent;
  padding: 0.9rem 1.1rem;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
  transition: transform 160ms ease, border-color 160ms ease, background 160ms ease, opacity 160ms ease;
}

.primaryButton { background: var(--color-gold); color: var(--color-dark); }
.secondaryButton { margin-top: 0.35rem; border-color: color-mix(in srgb, var(--color-gold) 52%, transparent); background: color-mix(in srgb, var(--color-gold) 12%, transparent); color: var(--color-text-primary); }
.primaryButton:hover:not(:disabled), .secondaryButton:hover:not(:disabled) { transform: translateY(-1px); }
.primaryButton:focus-visible, .secondaryButton:focus-visible, .input:focus-visible { outline: 3px solid color-mix(in srgb, var(--color-gold) 66%, white); outline-offset: 3px; }
.primaryButton:disabled, .secondaryButton:disabled { cursor: wait; opacity: 0.62; }

.form { display: grid; gap: 0.65rem; }
.label { margin-top: 0.2rem; color: var(--color-text-secondary); font-size: 0.86rem; font-weight: 650; }
.input { width: 100%; min-height: 3.25rem; border: 1px solid var(--color-border-soft); border-radius: 0.9rem; padding: 0.8rem 0.95rem; background: rgb(10 22 40 / 52%); color: var(--color-text-primary); font: inherit; font-size: 1rem; }
.input::placeholder { color: rgb(222 230 235 / 45%); }

.message {
  border: 1px solid var(--color-border-soft);
  border-radius: 1rem;
  padding: 0.9rem 1rem;
  font-size: 0.9rem;
  line-height: 1.5;
  background: color-mix(in srgb, var(--color-dark) 68%, transparent);
}
.message[data-kind='error'] { border-color: color-mix(in srgb, #d99393 62%, transparent); color: #ffdede; }
.message[data-kind='success'] { border-color: color-mix(in srgb, #8cc9b5 62%, transparent); color: #dcfff4; }
.message[data-kind='info'] { color: var(--color-text-secondary); }

.footer {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding-top: 1rem;
  color: var(--color-text-tertiary);
  font-size: 0.78rem;
}

@media (max-width: 800px) {
  .grid { grid-template-columns: 1fr; }
  .card { min-height: auto; }
  .footer { flex-direction: column; }
}

@media (prefers-reduced-motion: reduce) {
  .primaryButton, .secondaryButton { transition: none; }
}

.footer a{color:#bfad81;text-decoration:none;font-weight:800}.footer a:hover{text-decoration:underline}.footer a:focus-visible{outline:3px solid #bfad81;outline-offset:3px;border-radius:4px}
